// Frontend mirror of the canonical all-day boundary resolution in
// functions/records/recordDateTimeUtils.js (PRs #233/#235): a local date
// resolves to the local start of that date — midnight, the first valid
// instant of the date when a DST gap skips midnight, or the earlier instant
// when midnight occurs twice — and a date skipped entirely by the timezone
// is invalid. The backend module is not importable here (separate CommonJS
// package depending on firebase-admin), so this copy must stay logic- and
// fixture-identical: keep it in sync with
// functions/records/__tests__/recordDateTimeUtils.test.js.

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const formatterCache = new Map();

const PROBE_SPAN_MS = 24 * 60 * 60 * 1000;

// Bound for scanning past calendar dates a timezone skips entirely. tzdb's
// largest forward jump is 24 hours (dateline moves), which skips exactly one
// date, so the bound only guards termination.
const MAX_NEXT_DATE_SCAN = 3;

/**
 * Return whether a timezone string is accepted by the platform IANA database.
 * @param {*} value
 * @return {boolean}
 */
function isValidIanaTimeZone(value) {
  if (typeof value !== "string" || !value.trim()) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value.trim() });
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Return a cached formatter for timezone math.
 * @param {string} timeZone
 * @return {Intl.DateTimeFormat}
 */
function getDateTimeFormatter(timeZone) {
  if (!formatterCache.has(timeZone)) {
    formatterCache.set(
      timeZone,
      new Intl.DateTimeFormat("en-US", {
        calendar: "gregory",
        numberingSystem: "latn",
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
      })
    );
  }
  return formatterCache.get(timeZone);
}

/**
 * Return date/time parts for an instant in the requested timezone.
 * @param {Date} date
 * @param {string} timeZone
 * @return {Object}
 */
function zonedParts(date, timeZone) {
  const parts = {};
  for (const part of getDateTimeFormatter(timeZone).formatToParts(date)) {
    if (part.type !== "literal") {
      parts[part.type] = Number(part.value);
    }
  }
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour === 24 ? 0 : parts.hour,
    minute: parts.minute,
    second: parts.second,
  };
}

/**
 * Return the wall-clock reading of an instant in a timezone, encoded as a
 * UTC-epoch millisecond value. Exact to the second, so sub-minute historical
 * offsets survive where minute rounding would corrupt them.
 * @param {number} utcMs
 * @param {string} timeZone
 * @return {number}
 */
function zonedWallClockMs(utcMs, timeZone) {
  const parts = zonedParts(new Date(utcMs), timeZone);
  return Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
}

/**
 * Parse a YYYY-MM-DD string with calendar validation.
 * @param {*} value
 * @return {{ok: true, parts: Object}|{ok: false}}
 */
function parseDateOnly(value) {
  if (typeof value !== "string") return { ok: false };
  const match = value.trim().match(DATE_ONLY_RE);
  if (!match) return { ok: false };
  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const test = new Date(Date.UTC(year, month - 1, day));
  if (
    test.getUTCFullYear() !== year ||
    test.getUTCMonth() !== month - 1 ||
    test.getUTCDate() !== day
  ) {
    return { ok: false };
  }
  return { ok: true, parts: { year, month, day } };
}

/**
 * Convert local date/time parts in a timezone to a UTC epoch-ms value.
 *
 * Disambiguation follows Temporal "compatible": a wall time that occurs twice
 * resolves to the earlier instant; a wall time skipped by a DST gap resolves
 * to the first valid instant after the gap, accepted only when that instant
 * still falls on the requested calendar date (a date skipped entirely by the
 * timezone stays invalid).
 * @param {Object} params
 * @param {Object} params.parts
 * @param {string} params.timeZone
 * @return {{ok: true, utcMs: number}|{ok: false}}
 */
function localDateTimeToUtcMs({ parts, timeZone }) {
  if (!isValidIanaTimeZone(timeZone)) return { ok: false };
  const wallMs = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour || 0,
    parts.minute || 0,
    parts.second || 0
  );
  const candidateOffsetsMs = new Set();
  const probes = [wallMs - PROBE_SPAN_MS, wallMs, wallMs + PROBE_SPAN_MS];
  for (const probeMs of probes) {
    candidateOffsetsMs.add(zonedWallClockMs(probeMs, timeZone) - probeMs);
  }
  const validUtcMsValues = [];
  for (const offsetMs of candidateOffsetsMs) {
    const utcMs = wallMs - offsetMs;
    if (zonedWallClockMs(utcMs, timeZone) === wallMs) {
      validUtcMsValues.push(utcMs);
    }
  }
  if (validUtcMsValues.length > 0) {
    return { ok: true, utcMs: Math.min(...validUtcMsValues) };
  }
  // DST gap: the transition instant is the first instant whose wall clock
  // has reached the requested wall time, and a gap of size
  // maxOffset - minOffset brackets it in (wallMs - max, wallMs - min].
  const offsets = [...candidateOffsetsMs];
  let lowUtcMs = wallMs - Math.max(...offsets);
  let highUtcMs = wallMs - Math.min(...offsets);
  if (
    !(lowUtcMs < highUtcMs) ||
    zonedWallClockMs(lowUtcMs, timeZone) >= wallMs ||
    zonedWallClockMs(highUtcMs, timeZone) < wallMs
  ) {
    return { ok: false };
  }
  while (highUtcMs - lowUtcMs > 1) {
    const midUtcMs = lowUtcMs + Math.floor((highUtcMs - lowUtcMs) / 2);
    if (zonedWallClockMs(midUtcMs, timeZone) >= wallMs) {
      highUtcMs = midUtcMs;
    } else {
      lowUtcMs = midUtcMs;
    }
  }
  const landed = zonedParts(new Date(highUtcMs), timeZone);
  if (
    landed.year !== parts.year ||
    landed.month !== parts.month ||
    landed.day !== parts.day
  ) {
    return { ok: false };
  }
  return { ok: true, utcMs: highUtcMs };
}

/**
 * Resolve date parts to the canonical local start of that date.
 * @param {{year: number, month: number, day: number}} parts
 * @param {string} timeZone
 * @return {{ok: true, utcMs: number}|{ok: false}}
 */
function datePartsToUtcMs(parts, timeZone) {
  return localDateTimeToUtcMs({
    parts: { ...parts, hour: 0, minute: 0, second: 0 },
    timeZone,
  });
}

/**
 * Convert YYYY-MM-DD in a timezone to the UTC Date of the canonical local
 * start of that date: midnight, the first valid instant of the date when a
 * DST gap skips midnight, or the earlier instant when midnight occurs twice.
 * A malformed date, an invalid timezone, or a date skipped entirely by the
 * timezone returns {ok: false}.
 * @param {string} dateText
 * @param {string} timeZone
 * @return {{ok: true, date: Date}|{ok: false}}
 */
export function dateOnlyToUtcDate(dateText, timeZone) {
  const parsed = parseDateOnly(dateText);
  if (!parsed.ok) return { ok: false };
  const resolved = datePartsToUtcMs(parsed.parts, timeZone);
  if (!resolved.ok) return { ok: false };
  return { ok: true, date: new Date(resolved.utcMs) };
}

/**
 * Convert an inclusive all-day end date to its exclusive stored boundary:
 * the canonical local start of the first resolvable calendar date after
 * dateText — the first instant after the last day of the event. Scanning
 * past a fully skipped next date keeps the boundary canonical (it equals
 * dateOnlyToUtcDate of the landed date). Returns {ok: false} when dateText
 * itself is malformed or fully skipped, the timezone is invalid, or the
 * exclusive end would land past year 9999 — boundary dates are four-digit
 * local dates, so later boundaries fail backend canonical validation.
 * @param {string} dateText
 * @param {string} timeZone
 * @return {{ok: true, date: Date}|{ok: false}}
 */
export function dateOnlyExclusiveEndToUtcDate(dateText, timeZone) {
  const parsed = parseDateOnly(dateText);
  if (!parsed.ok) return { ok: false };
  if (!datePartsToUtcMs(parsed.parts, timeZone).ok) return { ok: false };
  const { year, month, day } = parsed.parts;
  for (let offset = 1; offset <= MAX_NEXT_DATE_SCAN; offset += 1) {
    const next = new Date(Date.UTC(year, month - 1, day + offset));
    // Scan dates strictly increase, so the first date past year 9999 ends
    // the search.
    if (next.getUTCFullYear() > 9999) return { ok: false };
    const resolved = datePartsToUtcMs(
      {
        year: next.getUTCFullYear(),
        month: next.getUTCMonth() + 1,
        day: next.getUTCDate(),
      },
      timeZone
    );
    if (resolved.ok) {
      return { ok: true, date: new Date(resolved.utcMs) };
    }
  }
  return { ok: false };
}

/**
 * Convert a stored exclusive all-day end boundary back to the inclusive end
 * date for display: the local date of the instant one millisecond before the
 * boundary. That instant is the last one inside the range, so its date is the
 * last resolvable calendar date strictly before the boundary — the exact
 * inverse of dateOnlyExclusiveEndToUtcDate even when the timezone skips the
 * preceding date entirely. Returns {ok: false} for an invalid date or
 * timezone, or a local date past year 9999 (possible only for non-canonical
 * stored boundaries).
 *
 * Frontend-only display inverse — no backend counterpart; the module-header
 * mirror contract covers the resolver logic above (the backend instead checks
 * canonicality via timestampToAllDayBoundaryDateText).
 * @param {Date} date
 * @param {string} timeZone
 * @return {{ok: true, dateText: string}|{ok: false}}
 */
export function utcExclusiveEndToInclusiveDateText(date, timeZone) {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) {
    return { ok: false };
  }
  if (!isValidIanaTimeZone(timeZone)) return { ok: false };
  const parts = zonedParts(new Date(date.getTime() - 1), timeZone.trim());
  if (parts.year > 9999) return { ok: false };
  const dateText = `${String(parts.year).padStart(4, "0")}-${String(
    parts.month
  ).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
  return { ok: true, dateText };
}
